import { MANAGED_CREDENTIAL } from './AiGateway';

/**
 * The credential the AI features run on, in either of the app's two deployment shapes.
 *
 * **Unmanaged** is the original single-user tool: the key lives in `localStorage` and goes from the
 * browser straight to `api.openai.com`, which accepts cross-origin requests. No backend to run,
 * nothing to deploy. It does mean the key is readable by anything running on this page, so it
 * should be scoped to this use and rotated like any other browser-held secret.
 *
 * **Managed** is the hosted deployment. There is no key in the browser at all — the server holds
 * one and attaches it per request, authenticated by the session cookie. The store still answers
 * "yes, AI is available" so the dozen places that gate on {@link ApiKeyStore.has} keep working, but
 * what {@link ApiKeyStore.get} returns is {@link MANAGED_CREDENTIAL}, a sentinel that `AiGateway`
 * drops on the floor: in proxy mode it builds no `Authorization` header, so the sentinel cannot be
 * transmitted even by accident.
 *
 * Every read and write goes through this one module, which is what made adding the second mode a
 * change to this file, `AiGateway`, and nothing else.
 */

const STORAGE_KEY = 'landscape-glade.openai-key';

export class ApiKeyStore {
  private cached: string | null = null;
  private loaded = false;
  private managed = false;
  private managedHint: string | null = null;
  private readonly listeners = new Set<() => void>();

  /**
   * Hand credential duty to the server.
   *
   * `hint` describes what the account is drawing on — the operator's allowance, or the user's own
   * stored key — and is what the Enhance panel shows in place of the key field.
   */
  setManaged(managed: boolean, hint: string | null = null): void {
    if (this.managed === managed && this.managedHint === hint) return;
    this.managed = managed;
    this.managedHint = hint;
    this.emit();
  }

  /** Whether the server is holding the credential for this session. */
  get isManaged(): boolean {
    return this.managed;
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** The credential to run with, or null when none is available. */
  get(): string | null {
    if (this.managed) return MANAGED_CREDENTIAL;
    if (!this.loaded) {
      try {
        this.cached = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
      } catch {
        // Storage disabled — the key simply cannot be remembered.
        this.cached = null;
      }
      this.loaded = true;
    }
    return this.cached;
  }

  has(): boolean {
    const key = this.get();
    return key !== null && key.length > 0;
  }

  /** Store a key, or clear it when given blank input. */
  set(key: string): void {
    const trimmed = key.trim();
    if (trimmed.length === 0) {
      this.clear();
      return;
    }
    this.cached = trimmed;
    this.loaded = true;
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, trimmed);
    } catch {
      // Non-fatal: the key holds for this session only.
    }
    this.emit();
  }

  clear(): void {
    this.cached = null;
    this.loaded = true;
    try {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to do — the in-memory copy is already gone.
    }
    this.emit();
  }

  /** A redacted form safe to show in the UI: `sk-…a91f`, or the account description when managed. */
  masked(): string | null {
    if (this.managed) return this.managedHint ?? 'managed by your account';
    const key = this.get();
    if (key === null || key.length === 0) return null;
    if (key.length <= 8) return '…';
    return `${key.slice(0, 3)}…${key.slice(-4)}`;
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

/** Shape check only — the API is the authority on whether a key actually works. */
export function looksLikeApiKey(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith('sk-') && trimmed.length >= 20;
}
